// src/components/kuestion/campus/Students.jsx
//
// Roster on the left, one student on the right. Every row and every heading
// shows the FULL login ID (contract §5): that complete string is what the
// student types, and a row showing only the six-character code is a support
// call waiting to happen.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useCampus } from './CampusContext';
import { Loading, Alert, Stat, RuleHead, CopyButton, Dialog } from '../shared/Bits';
import { SearchIcon, ChevronLeft, ChevronRight, WarnIcon, RefreshIcon, EditIcon, LockIcon } from '../shared/Icons';
import { fmtDate, fullName, initials, isProctored, modeLabel } from '../shared/format';
import { generatePassword, passwordProblems } from '../shared/password';
import { kq } from '../../../config/routes';

const PAGE = 8;

export default function Students() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { org, students, studentsLoading, reloadStudents, assignments, api } = useCampus();

  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [dialog, setDialog] = useState(null); // 'edit' | 'reset' | 'delete'
  const [flash, setFlash] = useState(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return students.filter((s) => {
      if (filter === 'active' && s.is_active === false) return false;
      if (filter === 'disabled' && s.is_active !== false) return false;
      if (!needle) return true;
      return `${fullName(s)} ${s.login_id || ''} ${s.student_code || ''}`.toLowerCase().includes(needle);
    });
  }, [students, q, filter]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const safePage = Math.min(page, pages - 1);
  const slice = filtered.slice(safePage * PAGE, safePage * PAGE + PAGE);

  useEffect(() => { setPage(0); }, [q, filter]);

  const current = useMemo(() => {
    if (id) return students.find((s) => String(s.id) === String(id)) || null;
    return null;
  }, [students, id]);

  // Land on the first student when none is in the URL.
  useEffect(() => {
    if (!id && students.length) navigate(kq(`students/${students[0].id}`), { replace: true });
  }, [id, students, navigate]);

  // Sittings for the selected student, read off the assignment list's
  // per-student status where the server provides it.
  const sittings = useMemo(() => {
    if (!current) return [];
    const out = [];
    assignments.forEach((a) => {
      const row = (a.students || []).find((s) => String(s.student_id ?? s.id) === String(current.id));
      if (row && (row.status === 'submitted' || row.submitted_at)) {
        out.push({ key: `${a.id}-${current.id}`, name: a.template_name, mode: a.mode, at: row.submitted_at, score: row.score, total: row.total, pct: row.percentage });
      }
    });
    return out.sort((x, y) => new Date(y.at || 0) - new Date(x.at || 0));
  }, [assignments, current]);

  const active = students.filter((s) => s.is_active !== false).length;

  return (
    <div className="kqc-page kqc-page--flush">
      <div className="kqc-split">
        <div className="kqc-split__side">
          <div className="kqc-split__head">
            <div className="kqc-row" style={{ marginBottom: 12 }}>
              <h1 style={{ fontSize: 19 }}>Students</h1>
              <span className="kqc-count">{students.length}</span>
              <span className="kqc-spacer" />
              <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate(kq('students/new'))} data-tour="ros-add">Add</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(kq('students/import'))}>Import</button>
            </div>
            <div className="kqc-search">
              <span><SearchIcon /></span>
              <input type="text" placeholder="Search name or login ID" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="kqc-chips">
              <button type="button" className={filter === 'all' ? 'is-active' : ''} onClick={() => setFilter('all')}>All {students.length}</button>
              <button type="button" className={filter === 'active' ? 'is-active' : ''} onClick={() => setFilter('active')}>Active {active}</button>
              <button type="button" className={filter === 'disabled' ? 'is-active' : ''} onClick={() => setFilter('disabled')}>Disabled {students.length - active}</button>
            </div>
          </div>

          <div className="kqc-split__scroll">
            {studentsLoading && students.length === 0 ? <div style={{ padding: '0 20px' }}><Loading label="Loading roster" /></div>
              : slice.length === 0 ? (
                <div style={{ padding: '24px 20px' }} className="kqc-small kqc-muted">
                  {students.length === 0 ? 'No students yet. Add one or import a CSV.' : 'No students match.'}
                </div>
              ) : slice.map((s) => (
                <button type="button" key={s.id} className={`kqc-roster__row${current && current.id === s.id ? ' is-selected' : ''}`} onClick={() => navigate(kq(`students/${s.id}`))}>
                  <span className="kqc-avatar kqc-avatar--soft">{initials(s.first_name, s.last_name)}</span>
                  <span className="kqc-roster__body">
                    <span className="kqc-roster__name">{fullName(s)}</span>
                    <span className="kqc-roster__login">{s.login_id || `${org?.prefix}-${s.student_code}`}</span>
                  </span>
                  <span className={`kqc-dot${s.is_active === false ? ' is-off' : ''}`} title={s.is_active === false ? 'Disabled' : 'Active'} />
                </button>
              ))}
          </div>

          <div className="kqc-split__foot">
            <span>{filtered.length === 0 ? '0' : `${safePage * PAGE + 1}–${Math.min(filtered.length, (safePage + 1) * PAGE)}`} of {filtered.length}</span>
            <span className="kqc-spacer" />
            <button type="button" className="kqc-iconbtn kqc-iconbtn--sm" aria-label="Previous page" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}><ChevronLeft /></button>
            <button type="button" className="kqc-iconbtn kqc-iconbtn--sm" aria-label="Next page" disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)}><ChevronRight /></button>
          </div>
        </div>

        <div className="kqc-split__main">
          {flash && <Alert kind={flash.kind} onClose={() => setFlash(null)}>{flash.msg}</Alert>}

          {!current ? (
            students.length === 0 ? (
              <>
                <div className="kqc-kicker" style={{ marginBottom: 8 }}>Roster</div>
                <h2 className="kqc-h1" style={{ marginBottom: 10 }}>No students yet</h2>
                <p className="kqc-lede" style={{ marginBottom: 22 }}>Students never sign themselves up. You create them here, one at a time or from a CSV, and hand each one their login ID and password.</p>
                <div className="kqc-row">
                  <button type="button" className="btn btn-primary" onClick={() => navigate(kq('students/new'))}>Add a student</button>
                  <button type="button" className="btn btn-secondary" onClick={() => navigate(kq('students/import'))}>Import a class</button>
                </div>
              </>
            ) : <Loading label="Loading student" />
          ) : (
            <>
              <div className="kqc-pagehead" style={{ alignItems: 'flex-start', marginBottom: 22 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="kqc-kicker" style={{ marginBottom: 8 }}>Student</div>
                  <h2 className="kqc-h1" style={{ fontSize: 'clamp(24px,3vw,36px)', marginBottom: 10 }}>{fullName(current)}</h2>
                  <div className="kqc-row">
                    <span className="kqc-loginid kqc-loginid--big">{current.login_id}</span>
                    <CopyButton text={current.login_id} label="Copy" />
                  </div>
                  <div className="kqc-small kqc-muted" style={{ marginTop: 8 }}>This whole string is what the student types. Case does not matter.</div>
                </div>
                <span className="kqc-spacer" />
                <div className="kqc-row" style={{ gap: 8 }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDialog('edit')}>Edit</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDialog('reset')}>Reset password</button>
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => setDialog('delete')}>Delete</button>
                </div>
              </div>

              <div className="kqc-stats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                <Stat label="Student code" value={current.student_code} size="sm" />
                <Stat label="Status" value={current.is_active === false ? 'Disabled' : 'Active'} size="sm" />
                <Stat label="Sittings" value={sittings.length} size="sm" />
                <Stat label="Added" value={fmtDate(current.created_at)} size="sm" />
              </div>

              <RuleHead title="Sittings" as="h3" right={<button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 0 }} onClick={() => navigate(kq(`analytics/students/${current.id}`))}>Full report &rarr;</button>} />
              {sittings.length === 0 ? (
                <div className="kqc-empty">No completed sittings yet. Assigned exams and their results will appear here once submitted.</div>
              ) : (
                <div className="table-wrap">
                  <table className="table" style={{ minWidth: 520 }}>
                    <thead><tr><th>Exam</th><th>Mode</th><th>Sat</th><th>Score</th></tr></thead>
                    <tbody>
                      {sittings.map((s) => (
                        <tr key={s.key}>
                          <td>{s.name}</td>
                          <td><span className={`tag ${isProctored(s.mode) ? 'tag-accent' : 'tag-neutral'}`}>{modeLabel(s.mode)}</span></td>
                          <td>{fmtDate(s.at)}</td>
                          <td><strong>{s.score != null && s.total != null ? `${s.score} / ${s.total}` : s.pct != null ? `${Math.round(s.pct)}%` : '—'}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {current && dialog === 'edit' && (
        <EditDialog student={current} api={api} onClose={() => setDialog(null)} onDone={async (msg) => { setDialog(null); setFlash({ kind: 'success', msg }); await reloadStudents(); }} />
      )}
      {current && dialog === 'reset' && (
        <ResetDialog student={current} api={api} onClose={() => setDialog(null)} />
      )}
      {current && dialog === 'delete' && (
        <DeleteDialog student={current} api={api} onClose={() => setDialog(null)} onDone={async () => {
          setDialog(null);
          setFlash({ kind: 'success', msg: `${fullName(current)} was deleted. The code ${current.student_code} can be used again.` });
          await reloadStudents();
          navigate(kq('students'), { replace: true });
        }} />
      )}
    </div>
  );
}

// ── Edit names / active state ────────────────────────────────────────────
function EditDialog({ student, api, onClose, onDone }) {
  const [first, setFirst] = useState(student.first_name || '');
  const [last, setLast] = useState(student.last_name || '');
  const [active, setActive] = useState(student.is_active !== false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!first.trim() || !last.trim()) { setError('Both names are required.'); return; }
    setBusy(true); setError('');
    try {
      await api.putJson(API_ENDPOINTS.CAMPUS_STUDENT(student.id), { first_name: first.trim(), last_name: last.trim(), is_active: active });
      onDone('Student updated.');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog title="Edit student" icon={<EditIcon />} onClose={busy ? null : onClose}>
      <h3>{fullName(student)}</h3>
      <p className="kqc-small kqc-muted" style={{ marginBottom: 16 }}>The login ID <strong>{student.login_id}</strong> cannot change — the code is theirs for as long as the account exists.</p>
      {error && <Alert kind="error">{error}</Alert>}
      <div className="kqc-row kqc-row--gap" style={{ alignItems: 'stretch', marginBottom: 14 }}>
        <div className="field" style={{ flex: '1 1 140px' }}><label htmlFor="ed-fn">First name</label><input id="ed-fn" className="input" value={first} onChange={(e) => setFirst(e.target.value)} disabled={busy} /></div>
        <div className="field" style={{ flex: '1 1 140px' }}><label htmlFor="ed-ln">Last name</label><input id="ed-ln" className="input" value={last} onChange={(e) => setLast(e.target.value)} disabled={busy} /></div>
      </div>
      <label className="kqc-checkline" style={{ marginBottom: 20 }}>
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={busy} />
        <span><strong>Active</strong> — a disabled student cannot log in, but keeps every result.</span>
      </label>
      <div className="kqc-row">
        <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
      </div>
    </Dialog>
  );
}

// ── Reset password ───────────────────────────────────────────────────────
function ResetDialog({ student, api, onClose }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const problems = pw ? passwordProblems(pw) : [];

  const submit = async () => {
    if (pw && problems.length) { setError(`The password needs ${problems.join(', ')}.`); return; }
    setBusy(true); setError('');
    try {
      const data = await api.postJson(API_ENDPOINTS.CAMPUS_STUDENT_PASSWORD(student.id), pw ? { password: pw } : {});
      setResult(data?.generated_password || pw);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog title="Reset password" icon={<LockIcon />} onClose={busy ? null : onClose}>
      <h3>{fullName(student)}</h3>
      {result ? (
        <>
          <p className="kqc-small" style={{ color: 'var(--color-neutral-800)', marginBottom: 14 }}>The old password no longer works. Hand this one over now — it is shown once and cannot be retrieved.</p>
          <div className="kqc-handout">Login ID</div>
          <div className="is-id">{student.login_id}</div>
          <div className="kqc-handout">New password</div>
          <div className="is-pw">{result}</div>
          <div className="kqc-row">
            <CopyButton text={`${student.login_id}\t${result}`} label="Copy both" ink />
            <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </>
      ) : (
        <>
          <p className="kqc-small kqc-muted" style={{ marginBottom: 16 }}>Students have no email and cannot reset their own password. Generate one, or type one that meets the same policy — 12–20 characters with upper, lower, number and symbol.</p>
          {error && <Alert kind="error">{error}</Alert>}
          <div className="kqc-row" style={{ marginBottom: 14 }}>
            <button type="button" className="kqc-genbtn" onClick={() => setPw(generatePassword())} disabled={busy}><RefreshIcon />Generate</button>
            <span className="kqc-small kqc-muted">or type one below</span>
          </div>
          <div className="field" style={{ marginBottom: 20 }}>
            <label htmlFor="rs-pw">New password</label>
            <input id="rs-pw" className="input kqc-mono" type="text" autoComplete="off" spellCheck={false} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Leave blank to let the server generate one" disabled={busy} />
            {pw && problems.length > 0 && <div className="kqc-small kqc-accent-ink" style={{ marginTop: 6 }}>Needs {problems.join(', ')}.</div>}
          </div>
          <div className="kqc-row">
            <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Resetting…' : 'Reset password'}</button>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          </div>
        </>
      )}
    </Dialog>
  );
}

// ── Hard delete with typed confirmation (contract §5.2) ──────────────────
function DeleteDialog({ student, api, onClose, onDone }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ok = text.trim().toUpperCase() === String(student.student_code).toUpperCase();

  const run = async () => {
    if (!ok) return;
    setBusy(true); setError('');
    try {
      await api.del(API_ENDPOINTS.CAMPUS_STUDENT(student.id));
      onDone();
    } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <Dialog title="Permanent deletion" danger icon={<WarnIcon />} onClose={busy ? null : onClose}>
      <h3>Delete {fullName(student)}?</h3>
      <p className="kqc-small" style={{ color: 'var(--color-neutral-800)', marginBottom: 14 }}>There is no recovery. Deleting this student destroys:</p>
      <div className="kqc-dialog__list">
        <div>Their account and the login <strong>{student.login_id}</strong></div>
        <div><strong>All exam results</strong>, including completed proctored sittings</div>
        <div>Their contribution to cohort analytics</div>
        <div>The code <strong>{student.student_code}</strong> — it becomes reusable</div>
        <div style={{ background: 'var(--color-accent-100)' }}>Because the code returns to the pool, <strong>any printed handout carrying {student.login_id} would let a future student sign in</strong>. Destroy it.</div>
      </div>
      {error && <Alert kind="error">{error}</Alert>}
      <div className="field" style={{ marginBottom: 18 }}>
        <label htmlFor="del-confirm">Type <strong>{student.student_code}</strong> to confirm</label>
        <input id="del-confirm" className="input kqc-mono" type="text" value={text} onChange={(e) => setText(e.target.value)} placeholder={student.student_code} autoComplete="off" disabled={busy} />
      </div>
      <div className="kqc-row">
        <button type="button" className="btn btn-primary" disabled={!ok || busy} onClick={run}>{busy ? 'Deleting…' : 'Delete permanently'}</button>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Keep the student</button>
      </div>
    </Dialog>
  );
}
